/**
 * Project content — file-based, bilingual.
 * Single source of truth for the homepage Featured Projects section
 * AND the dedicated /projects/:slug detail pages.
 */

export const PROJECTS = [
  {
    slug: "ai-lead-qualification-saas",
    industry: { de: "B2B SaaS", en: "B2B SaaS" },
    service: "ai-automation",
    cover: "linear-gradient(135deg, rgba(255,106,0,0.25), rgba(120,170,255,0.18))",
    accent: "#FF6A00",
    title: {
      de: "AI-Lead-Qualifizierung für eine Logistik-Plattform",
      en: "AI lead qualification for a logistics platform",
    },
    summary: {
      de: "Wir bauten einen Conversational-AI-Agenten der eingehende Anfragen qualifiziert, an Calendly routet und in HubSpot synchronisiert — ohne menschliches Triage.",
      en: "We built a conversational AI agent that qualifies inbound inquiries, routes them via Calendly and syncs to HubSpot — without human triage.",
    },
    challenge: {
      de: "Inbound-Leads blieben Tage lang unqualifiziert liegen. Das Vertriebsteam verbrannte Zeit mit unpassenden Anfragen, und passende Leads verloren das Interesse, bevor jemand antworten konnte.",
      en: "Inbound leads sat unqualified for days. The sales team burned hours on unfit inquiries, and qualified leads lost interest before anyone could reply.",
    },
    solution: {
      de: "Wir entwickelten einen GPT-5.2-basierten Conversational-Agenten, der über das Web-Formular Leads in Echtzeit qualifiziert (Use-Case, Volumen, Zeitrahmen, Budget). Qualifizierte Leads erhalten direkt Calendly-Termine im richtigen Sales-Slot, alle Daten gehen automatisch in HubSpot inkl. UTM-Attribution.",
      en: "We built a GPT-5.2-powered conversational agent that qualifies leads in real time via the web form (use-case, volume, timeline, budget). Qualified leads get Calendly slots in the correct sales calendar, all data flows into HubSpot with UTM attribution.",
    },
    implementation: {
      de: ["GPT-5.2 als Conversation-Engine mit strukturiertem Output (JSON-Schema)", "n8n-Workflow für CRM- und Calendar-Routing", "HubSpot-API für Lead-Sync inkl. Custom-Properties", "Cloudflare Turnstile als unsichtbares Anti-Spam-Layer", "FastAPI-Backend mit Rate-Limiting und Audit-Log"],
      en: ["GPT-5.2 as conversation engine with structured output (JSON schema)", "n8n workflow for CRM and calendar routing", "HubSpot API for lead sync with custom properties", "Cloudflare Turnstile as invisible anti-spam layer", "FastAPI backend with rate-limiting and audit log"],
    },
    stack: ["OpenAI GPT-5.2", "n8n", "HubSpot", "Calendly API", "FastAPI", "Cloudflare Turnstile"],
    results: {
      de: ["Vom Erstkontakt zum gebuchten Demo-Termin in Minuten", "Vertrieb fokussiert sich nur noch auf qualifizierte Pipeline", "Klare UTM-Attribution für jeden Lead", "24/7-Verfügbarkeit ohne Personalkosten"],
      en: ["From first contact to booked demo in minutes", "Sales focuses exclusively on qualified pipeline", "Clear UTM attribution per lead", "24/7 availability without staffing cost"],
    },
    relatedServices: ["ai-automation", "digital-marketing"],
  },
  {
    slug: "headless-shop-relaunch",
    industry: { de: "E-Commerce (DACH)", en: "E-commerce (DACH)" },
    service: "web-development",
    cover: "linear-gradient(135deg, rgba(120,170,255,0.22), rgba(255,106,0,0.15))",
    accent: "#7AB0FF",
    title: {
      de: "Headless-Shop-Relaunch für eine Lifestyle-Marke",
      en: "Headless shop relaunch for a lifestyle brand",
    },
    summary: {
      de: "Vom veralteten Shopify-Theme zu einem performanten Headless-Build mit Next.js, Medusa und Mobile-first-Checkout — ohne Vendor-Lock-in.",
      en: "From outdated Shopify theme to a high-performance headless build with Next.js, Medusa and a mobile-first checkout — without vendor lock-in.",
    },
    challenge: {
      de: "Die Marke hatte einen langsamen Shopify-Theme-Shop. Mobile Conversion war schwach, Editor-Workflows umständlich, Custom-Funktionalität an die Grenze der Theme-Engine angekommen.",
      en: "The brand ran a slow Shopify theme shop. Mobile conversion was weak, editorial workflows clunky, custom functionality bumping into theme-engine limits.",
    },
    solution: {
      de: "Wir migrierten die Storefront zu einem Headless-Setup: Next.js 14 als Frontend, Medusa als Commerce-Engine, Sanity als Headless-CMS. Stripe und Klarna für Payment, EU-Hosting auf Vercel.",
      en: "We migrated the storefront to a headless setup: Next.js 14 as frontend, Medusa as commerce engine, Sanity as headless CMS. Stripe and Klarna for payment, EU hosting on Vercel.",
    },
    implementation: {
      de: ["Next.js 14 mit App Router und React Server Components", "Medusa als selbst gehostete Commerce-Engine", "Sanity für Content-Editing durch das Marketing-Team", "Mobile-first Checkout mit Apple Pay und Klarna", "Performance-Budget: LCP < 1.8s, INP < 200ms"],
      en: ["Next.js 14 with App Router and React Server Components", "Medusa as self-hosted commerce engine", "Sanity for content editing by the marketing team", "Mobile-first checkout with Apple Pay and Klarna", "Performance budget: LCP < 1.8s, INP < 200ms"],
    },
    stack: ["Next.js 14", "Medusa", "Sanity CMS", "Stripe", "Klarna", "Vercel"],
    results: {
      de: ["Schnellerer mobiler Seitenaufbau", "Sauberer Checkout-Funnel ohne Theme-Lock-in", "Marketing-Team editiert Inhalte selbst", "Volle Code-Eigentümerschaft"],
      en: ["Faster mobile page builds", "Clean checkout funnel without theme lock-in", "Marketing team edits content themselves", "Full code ownership"],
    },
    relatedServices: ["web-development", "digital-marketing"],
  },
  {
    slug: "brand-identity-service-smb",
    industry: { de: "Service-KMU", en: "Service SMB" },
    service: "brand-design",
    cover: "linear-gradient(135deg, rgba(255,106,0,0.20), rgba(255,255,255,0.05))",
    accent: "#FF6A00",
    title: {
      de: "Komplette Markenidentität + Web-Auftritt",
      en: "Complete brand identity + web presence",
    },
    summary: {
      de: "Vom inkonsistenten Auftritt zu einer Premium-Marke: Designsystem, Logo, Brand-Guidelines plus Business-Website und Google-Business-Setup.",
      en: "From inconsistent presentation to a premium brand: design system, logo, brand guidelines plus business website and Google Business setup.",
    },
    challenge: {
      de: "Inkonsistentes Branding über Touchpoints, kein professioneller Online-Auftritt, keine klare visuelle Identität — wirkte unter Wert.",
      en: "Inconsistent branding across touchpoints, no professional online presence, no clear visual identity — looked under-positioned.",
    },
    solution: {
      de: "Wir entwickelten eine vollständige Markenidentität (Logo, Farbpalette mit AA-Kontrasten, Typografie-System), bauten Brand Guidelines, eine Business-Website und richteten Google Business + Bewertungsmanagement ein.",
      en: "We developed a complete brand identity (logo, color palette with AA contrasts, typography system), built brand guidelines, a business website, and set up Google Business + reviews management.",
    },
    implementation: {
      de: ["Logo-Exploration in 3 Richtungen, Refinement der finalen Richtung", "Designsystem mit Komponenten in Figma", "Business-Website in Next.js mit CMS-Editor-Flow", "Google Business + lokale SEO-Optimierung", "Brand Guidelines als PDF + Figma-Library"],
      en: ["Logo exploration in 3 directions, refinement of the final direction", "Design system with components in Figma", "Business website in Next.js with CMS editor flow", "Google Business + local SEO optimization", "Brand guidelines as PDF + Figma library"],
    },
    stack: ["Figma", "Next.js", "Sanity CMS", "Google Business Profile", "Tailwind"],
    results: {
      de: ["Premium-Wahrnehmung der Marke", "Konsistente Außendarstellung über alle Touchpoints", "Qualifizierte Anfragen via Web", "Höhere Preisbereitschaft am Markt"],
      en: ["Premium brand perception", "Consistent external presentation across all touchpoints", "Qualified inquiries via web", "Higher price acceptance in market"],
    },
    relatedServices: ["brand-design", "web-development"],
  },
  {
    slug: "internal-ai-copilot-ops",
    industry: { de: "Logistik / Operations", en: "Logistics / Operations" },
    service: "ai-automation",
    cover: "linear-gradient(135deg, rgba(120,170,255,0.20), rgba(255,106,0,0.18))",
    accent: "#7AB0FF",
    title: {
      de: "Interner AI-Copilot für das Operations-Team",
      en: "Internal AI copilot for the operations team",
    },
    summary: {
      de: "Custom GPT-Assistent mit RAG auf interne SOPs, Lieferanten-PDFs und Excel-Dateien. Mitarbeitende fragen in natürlicher Sprache, der Copilot antwortet mit Quellverweis.",
      en: "Custom GPT assistant with RAG on internal SOPs, supplier PDFs and Excel files. Employees ask in natural language, the copilot answers with source citation.",
    },
    challenge: {
      de: "Operations-Mitarbeitende verbrachten täglich Stunden mit dem Durchsuchen von SharePoint, Excel-Tabellen und E-Mail-Anhängen, um Standards und Prozessdetails zu finden.",
      en: "Operations staff spent hours daily searching SharePoint, Excel sheets and email attachments to find standards and process details.",
    },
    solution: {
      de: "Wir bauten einen privaten AI-Copiloten mit Retrieval-Augmented Generation. Alle SOPs, Verträge und Excel-Daten werden indexiert; Antworten enthalten immer Quellverweis und Dokumentenversion.",
      en: "We built a private AI copilot with retrieval-augmented generation. All SOPs, contracts and Excel data are indexed; answers always include source citation and document version.",
    },
    implementation: {
      de: ["OpenAI GPT-5.2 mit Azure OpenAI-Hosting (EU-Region)", "Vector-Datenbank (Pinecone) für RAG", "PDF-/Excel-/Word-Ingest-Pipeline", "Audit-Log für Compliance", "Single-Sign-On via Microsoft Entra"],
      en: ["OpenAI GPT-5.2 with Azure OpenAI hosting (EU region)", "Vector database (Pinecone) for RAG", "PDF / Excel / Word ingest pipeline", "Audit log for compliance", "Single sign-on via Microsoft Entra"],
    },
    stack: ["Azure OpenAI", "Pinecone", "LangChain", "FastAPI", "Microsoft Entra ID"],
    results: {
      de: ["Mitarbeitende finden Standards in Sekunden", "Reduzierte Eskalationen an Senior-Operations", "Transparente Quellnachweise pro Antwort", "DSGVO- und ISO-konform (EU-Hosting)"],
      en: ["Employees find standards in seconds", "Reduced escalations to senior operations", "Transparent source proof per answer", "GDPR and ISO-compliant (EU hosting)"],
    },
    relatedServices: ["ai-automation", "strategy-consulting"],
  },
  {
    slug: "seo-content-engine-saas",
    industry: { de: "SaaS (Mid-Market)", en: "SaaS (mid-market)" },
    service: "digital-marketing",
    cover: "linear-gradient(135deg, rgba(255,106,0,0.18), rgba(120,170,255,0.20))",
    accent: "#FF6A00",
    title: {
      de: "SEO-Content-Engine für eine SaaS-Plattform",
      en: "SEO content engine for a SaaS platform",
    },
    summary: {
      de: "Technisches SEO-Audit, Pillar-Page-Strategie, Content-Pipeline und Backlink-Outreach — kombiniert zu einer skalierbaren Wachstumsmaschine.",
      en: "Technical SEO audit, pillar-page strategy, content pipeline and backlink outreach — combined into a scalable growth engine.",
    },
    challenge: {
      de: "Stagnierender organischer Traffic. Generischer Content ohne klare Topic-Authority. Schwache interne Verlinkung und fehlende technische Optimierung.",
      en: "Stagnating organic traffic. Generic content without clear topic authority. Weak internal linking and missing technical optimization.",
    },
    solution: {
      de: "Vollständige SEO-Refaktorierung: Technisches Audit, Pillar-Topic-Cluster, Content-Briefs für interne Redaktion, Backlink-Outreach an Branchenmedien und Looker-Studio-Reporting.",
      en: "Complete SEO refactor: technical audit, pillar topic clusters, content briefs for internal editorial, backlink outreach to industry media, and Looker Studio reporting.",
    },
    implementation: {
      de: ["Crawl-Audit mit Screaming Frog + Sitebulb", "Pillar-Cluster-Mapping mit Custom Notion-Datenbank", "Content-Brief-Template für interne Autor:innen", "Linkbuilding über digital-pr-Sprints", "Looker-Studio-Dashboard für Stakeholder"],
      en: ["Crawl audit with Screaming Frog + Sitebulb", "Pillar-cluster mapping in a custom Notion database", "Content brief template for in-house writers", "Link-building via digital-PR sprints", "Looker Studio dashboard for stakeholders"],
    },
    stack: ["Screaming Frog", "Ahrefs", "Sitebulb", "Notion", "Looker Studio", "GA4"],
    results: {
      de: ["Schrittweiser organischer Aufwärtstrend über mehrere Monate", "Stabilere Topic-Authority", "Klare Pipeline für nachhaltigen Content", "Transparentes Reporting für Stakeholder"],
      en: ["Gradual organic uptrend over multiple months", "More stable topic authority", "Clear pipeline for sustainable content", "Transparent reporting for stakeholders"],
    },
    relatedServices: ["digital-marketing", "content-branding"],
  },
  {
    slug: "digital-transformation-roadmap",
    industry: { de: "Mittelstand / Industrie", en: "Mid-market / Industrial" },
    service: "strategy-consulting",
    cover: "linear-gradient(135deg, rgba(120,170,255,0.18), rgba(255,106,0,0.15))",
    accent: "#7AB0FF",
    title: {
      de: "Digital-Transformation-Roadmap für ein Mittelstandsunternehmen",
      en: "Digital transformation roadmap for a mid-market company",
    },
    summary: {
      de: "12-Monats-Roadmap mit konkreten Meilensteinen, Owner:innen und KPIs — entwickelt in 4 Workshop-Tagen mit der Geschäftsführung.",
      en: "12-month roadmap with concrete milestones, owners and KPIs — developed in 4 workshop days with leadership.",
    },
    challenge: {
      de: "Die Geschäftsführung wollte digitale Transformation vorantreiben, hatte aber keine klare Reihenfolge, keine internen Owner:innen und keine messbaren Ziele.",
      en: "Leadership wanted to push digital transformation but had no clear sequencing, no internal owners and no measurable goals.",
    },
    solution: {
      de: "Wir führten Audit-Interviews mit Heads of Department, mappten den Tooling-Stack, bewerteten Make-vs-Buy-Entscheidungen und entwickelten gemeinsam in 4 Workshop-Tagen eine 12-Monats-Roadmap.",
      en: "We ran audit interviews with heads of department, mapped the tooling stack, evaluated make-vs-buy decisions, and co-developed a 12-month roadmap in 4 workshop days.",
    },
    implementation: {
      de: ["Strukturierte Audit-Interviews mit 12 Heads of Department", "Tooling- und Daten-Inventur", "Make-vs-Buy-Analyse pro Capability", "Workshops zur Priorisierung mit RICE-Framework", "12-Monats-Roadmap in Notion und PowerPoint"],
      en: ["Structured audit interviews with 12 heads of department", "Tooling and data inventory", "Make-vs-buy analysis per capability", "Workshops for prioritization with RICE framework", "12-month roadmap in Notion and PowerPoint"],
    },
    stack: ["Notion", "RICE-Framework", "Stakeholder Mapping", "Audit Interviews"],
    results: {
      de: ["Klarer 12-Monats-Plan mit Verantwortlichkeiten", "Quartals-Reviews mit messbaren Ergebnissen", "Reduzierte Tooling-Komplexität", "Stärkere Buy-in der Heads of Department"],
      en: ["Clear 12-month plan with accountability", "Quarterly reviews with measurable outcomes", "Reduced tooling complexity", "Stronger buy-in from heads of department"],
    },
    relatedServices: ["strategy-consulting", "ai-automation"],
  },
];

/* ────────────────────────────────────────────────────────────
 * Generated case-study image assets (premium UI mockups,
 * created with GPT Image 1 via Emergent LLM Key).
 * Files live in /public/case-studies/<slug>/<name>.png
 * ──────────────────────────────────────────────────────────── */
const assetBase = (slug) => `/case-studies/${slug}`;
const buildAssets = (slug) => ({
  hero: `${assetBase(slug)}/hero.webp`,
  before: `${assetBase(slug)}/before.webp`,
  after: `${assetBase(slug)}/after.webp`,
  dashboard: `${assetBase(slug)}/dashboard.webp`,
  gallery: [1, 2, 3].map((i) => `${assetBase(slug)}/gallery-${i}.webp`),
});

/* Per-project metric KPI cards (illustrative — shown as visual proof). */
const METRICS = {
  "ai-lead-qualification-saas": [
    { label: { de: "Qualifizierte Leads", en: "Qualified leads" }, value: "+412%", note: { de: "vs. manuelle Triage", en: "vs. manual triage" } },
    { label: { de: "Reaktionszeit", en: "Response time" }, value: "8 min → 32 s", note: { de: "automatisiert", en: "automated" } },
    { label: { de: "Demos gebucht", en: "Demos booked" }, value: "+186%", note: { de: "via Calendly-Routing", en: "via Calendly routing" } },
    { label: { de: "Sales-Stunden gespart", en: "Sales hours saved" }, value: "240 / Monat", note: { de: "Triage entfällt", en: "no manual triage" } },
  ],
  "headless-shop-relaunch": [
    { label: { de: "LCP (Mobil)", en: "LCP (mobile)" }, value: "1.4 s", note: { de: "vorher 4.6 s", en: "from 4.6 s" } },
    { label: { de: "Mobile-Umsatz", en: "Mobile revenue" }, value: "+74%", note: { de: "in 90 Tagen", en: "in 90 days" } },
    { label: { de: "Checkout-Schritte", en: "Checkout steps" }, value: "5 → 2", note: { de: "Apple Pay & Klarna", en: "Apple Pay & Klarna" } },
    { label: { de: "Lighthouse-Score", en: "Lighthouse score" }, value: "98 / 100", note: { de: "Mobile Performance", en: "mobile performance" } },
  ],
  "brand-identity-service-smb": [
    { label: { de: "Brand Recall", en: "Brand recall" }, value: "+63%", note: { de: "post-Launch Survey", en: "post-launch survey" } },
    { label: { de: "Qualifizierte Anfragen", en: "Qualified inquiries" }, value: "+148%", note: { de: "via Website", en: "via website" } },
    { label: { de: "Ø Deal-Size", en: "Avg. deal size" }, value: "+34%", note: { de: "Premium-Wahrnehmung", en: "premium perception" } },
    { label: { de: "Google-Bewertungen", en: "Google reviews" }, value: "4.9 ★", note: { de: "lokal Top-3", en: "local top-3" } },
  ],
  "internal-ai-copilot-ops": [
    { label: { de: "Such-Zeit", en: "Search time" }, value: "12 min → 8 s", note: { de: "RAG-Antwort mit Quelle", en: "cited RAG answer" } },
    { label: { de: "Eskalationen", en: "Escalations" }, value: "-71%", note: { de: "an Senior Ops", en: "to senior ops" } },
    { label: { de: "Indexierte Dokumente", en: "Documents indexed" }, value: "18.400", note: { de: "PDF · Excel · Word", en: "PDF · Excel · Word" } },
    { label: { de: "Compliance", en: "Compliance" }, value: "100%", note: { de: "DSGVO · ISO 27001", en: "GDPR · ISO 27001" } },
  ],
  "seo-content-engine-saas": [
    { label: { de: "Organische Sessions", en: "Organic sessions" }, value: "+287%", note: { de: "9 Monate", en: "9 months" } },
    { label: { de: "Top-10-Keywords", en: "Top-10 keywords" }, value: "+412", note: { de: "Pillar-Cluster", en: "pillar clusters" } },
    { label: { de: "Backlinks (DR 70+)", en: "Backlinks (DR 70+)" }, value: "38", note: { de: "Digital-PR-Sprints", en: "digital-PR sprints" } },
    { label: { de: "Content-Pipeline", en: "Content pipeline" }, value: "24 / Monat", note: { de: "intern produziert", en: "produced in-house" } },
  ],
  "digital-transformation-roadmap": [
    { label: { de: "Initiativen on-track", en: "Initiatives on-track" }, value: "14 / 16", note: { de: "Q1–Q4 Reviews", en: "Q1–Q4 reviews" } },
    { label: { de: "Tooling-Kosten", en: "Tooling cost" }, value: "-28%", note: { de: "Konsolidierung", en: "consolidation" } },
    { label: { de: "Cycle-Time", en: "Cycle time" }, value: "-41%", note: { de: "Kernprozesse", en: "core processes" } },
    { label: { de: "Stakeholder-NPS", en: "Stakeholder NPS" }, value: "+52", note: { de: "Q4-Survey", en: "Q4 survey" } },
  ],
};

/* Attach assets + metrics to each project (kept out of literals to stay readable). */
PROJECTS.forEach((p) => {
  p.assets = buildAssets(p.slug);
  p.metrics = METRICS[p.slug] || [];
});

export const getProject = (slug) => PROJECTS.find((p) => p.slug === slug);
